import { ctaDesc, ctaTitle } from "@/lib/seo";
// Triple-key combo route: /translate/$language/$doctype/in/$location
// Phase P-TRIPLE-KEY — 18 languages × 15 doctypes × 14 hub locations
// ≈ 3,780 long-tail landing pages with Service + LegalService + FAQ +
// HowTo + Breadcrumb JSON-LD per page. Highest-intent search surface:
// "แปลสูติบัตรภาษาอังกฤษ ที่อโศก", "แปลทะเบียนสมรส german ที่ภูเก็ต".
//
// Locations are whitelisted (TRIPLE_KEY_LOCATIONS) to keep the matrix
// manageable and crawl-budget-friendly. Other (language, doctype, loc)
// combos fall back to the existing /translate/$language/in/$location and
// /translate/$language/$doctype pages.
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import {
  TRANSLATION_LANGUAGES,
  getTranslationLanguage,
} from "@/data/translation-languages";
import {
  TRANSLATION_DOCTYPES,
  getTranslationDocType,
} from "@/data/translation-doctypes";
import { getLocation } from "@/data/locations";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { FaqAccordion, ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import TripleKeyCrossLinks from "@/components/TripleKeyCrossLinks";
import {
  Languages, MapPin, FileCheck2, ShieldCheck, BadgeCheck, Clock,
  Phone, MessageCircle, ArrowRight, Globe2, Stamp,
} from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

// 14 hub locations with high commercial / expat search intent.
// Keep in sync with sitemap-translate.xml route below.
export const TRIPLE_KEY_LOCATIONS = [
  "bangkok-hq",
  "khonkaen",
  "udonthani",
  "nongkhai",
  "hub-asoke-corridor",
  "hub-sukhumvit-39",
  "hub-sukhumvit-49",
  "hub-sathorn-cbd",
  "hub-wireless-road",
  "hub-pattaya",
  "hub-phuket-patong",
  "hub-chiangmai-nimman",
  "hub-koh-samui",
  "hub-hua-hin",
] as const;

const LOC_SET = new Set<string>(TRIPLE_KEY_LOCATIONS);

export const Route = createFileRoute("/translate/$language/$doctype/in/$location")({
  loader: ({ params }) => {
    const lang = getTranslationLanguage(params.language);
    const doc = getTranslationDocType(params.doctype);
    const loc = getLocation(params.location);
    if (!lang || !doc || !loc) throw notFound();
    if (!LOC_SET.has(params.location)) throw notFound();
    return {
      langSlug: params.language,
      docSlug: params.doctype,
      locSlug: params.location,
    };
  },
  head: ({ params }) => {
    const l = getTranslationLanguage(params.language);
    const d = getTranslationDocType(params.doctype);
    const loc = getLocation(params.location);
    if (!l || !d || !loc || !LOC_SET.has(params.location)) {
      return { meta: [{ title: ctaTitle("ไม่พบหน้า", "/translate/$language/$doctype/in/$location", "th")}] };
    }
    const path = `/translate/${l.slug}/${d.slug}/in/${loc.slug}`;
    const url = `${SITE}${path}`;
    const price = (l.priceFromTh + d.surcharge).toLocaleString();
    const title = `แปล${d.nameTh} ${l.nameTh} ที่${loc.nameTh} รับรอง ฿${price}/หน้า | NYC`;
    const desc = `แปล${d.nameTh}เป็น${l.nameTh} สำหรับลูกค้า${loc.nameTh} — รับรองนักแปลขึ้นทะเบียน${l.isCertified ? " MoJ": ""}${l.isNaati ? " + NAATI": ""} · เสร็จ ${l.turnaround} · ใช้ยื่นสถานทูต/กงสุล/MFA ได้ทันที · มีบริการรับ-ส่งเอกสารถึง${loc.nameTh} — สอบถามฟรี ไม่มีข้อผูกมัด`;

    const faqs = [
      {
        q: `แปล${d.nameTh}เป็น${l.nameTh}ที่${loc.nameTh} ราคาเท่าไหร่?`,
        a: `เริ่ม ฿${price}/หน้า A4 — รับรองโดยนักแปลขึ้นทะเบียน + ตราประทับ NYC ใช้ยื่นได้ทันที สำหรับลูกค้า${loc.nameTh}มีบริการรับ-ส่งเอกสาร เร็วสุด ${l.turnaround}`,
      },
      {
        q: `${d.nameTh}แปลแล้วต้องรับรองอะไรเพิ่ม?`,
        a: `ทั่วไปต้องผ่านขั้นตอน: ${d.certifications.join(" → ")} เราจัดให้ครบทุกขั้นตอนในแพ็กเกจเดียว ไม่ต้องวิ่งเอง`,
      },
      ...d.faqExtra,
    ];

    const ratingKey = `translate/${l.slug}/${d.slug}/${loc.slug}`;
    const rating = aggregateRatingNode(ratingKey);

    return {
      meta: [
        { title: ctaTitle(title, "/translate/$language/$doctype/in/$location", "th") },
        { name: "description", content: ctaDesc(desc, "/translate/$language/$doctype/in/$location", "th")},
        { name: "keywords", content: [...l.keywords, ...d.keywordsTh, loc.nameTh, loc.nameEn].join(", ") },
        { property: "og:title", content: ctaTitle(title, "/translate/$language/$doctype/in/$location", "th")},
        { property: "og:description", content: ctaDesc(desc, "/translate/$language/$doctype/in/$location", "th")},
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "th_TH" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ctaTitle(title, "/translate/$language/$doctype/in/$location", "th")},
        { name: "twitter:description", content: desc },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th", href: url },
        { rel: "alternate", hrefLang: "en", href: `${SITE}/en/translate/${l.slug}/${d.slug}/in/${loc.slug}` },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `แปล${d.nameTh} (${d.nameEn}) → ${l.nameTh} · ${loc.nameTh}`,
              serviceType: "Certified document translation",
              category: d.category,
              areaServed: {
                "@type": "Place",
                name: loc.nameTh,
                ...(loc.lat && loc.lng
                  ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
                  : {}),
              },
              availableLanguage: [
                { "@type": "Language", name: l.nameEn, alternateName: l.nativeName, iso6391: l.iso639 },
                { "@type": "Language", name: "Thai", iso6391: "th" },
              ],
              provider: {
                "@type": "LegalService",
                "@id": `${SITE}/#org`,
                name: "NYC Legal & Notary Service",
                url: SITE,
                telephone: "+66832494999",
              },
              offers: {
                "@type": "Offer",
                price: l.priceFromTh + d.surcharge,
                priceCurrency: "THB",
                priceSpecification: {
                  "@type": "UnitPriceSpecification",
                  price: l.priceFromTh + d.surcharge,
                  priceCurrency: "THB",
                  unitText: "page",
                },
                availability: "https://schema.org/InStock",
              },
              aggregateRating: rating,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LegalService",
              "@id": `${url}#localbusiness`,
              name: `NYC Legal & Notary Service — แปล${d.nameTh} ${l.nameTh} (${loc.nameTh})`,
              url,
              telephone: "+66832494999",
              priceRange: "฿฿",
              address: {
                "@type": "PostalAddress",
                streetAddress: loc.address ?? "61 ซอยลาดพร้าว 95",
                addressLocality: loc.nameTh,
                postalCode: loc.postalCode ?? "10310",
                addressCountry: "TH",
              },
              areaServed: { "@type": "Place", name: loc.nameTh },
              openingHours: "Mo-Sa 09:00-18:00",
              aggregateRating: rating,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "HowTo",
              "@id": `${url}#howto`,
              name: `วิธีแปล${d.nameTh}เป็น${l.nameTh}สำหรับลูกค้า${loc.nameTh}`,
              step: [
                { "@type": "HowToStep", position: 1, name: "ส่งสแกน LINE @nycli", text: `ถ่ายภาพ${d.nameTh}ส่ง LINE @nycli รับใบเสนอราคา 15 นาที` },
                { "@type": "HowToStep", position: 2, name: "ยืนยันและชำระมัดจำ", text: `โอนมัดจำ 50% เพื่อเริ่มงานแปล${l.nameTh}` },
                { "@type": "HowToStep", position: 3, name: "แปล + รับรอง", text: `นักแปลขึ้นทะเบียนแปลและประทับตรา NYC พร้อมขั้นตอน ${d.certifications.join(" → ")}` },
                { "@type": "HowToStep", position: 4, name: "รับเอกสาร", text: `รับที่สำนักงาน หรือใช้ EMS/Messenger ส่งถึง${loc.nameTh}` },
              ],
              totalTime: `PT${l.turnaround.includes("ชั่วโมง") ? "4H" : "48H"}`,
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "แปลรายภาษา", item: `${SITE}/translate` },
                { "@type": "ListItem", position: 3, name: `แปล${l.nameTh}`, item: `${SITE}/translate/${l.slug}` },
                { "@type": "ListItem", position: 4, name: `แปล${d.nameTh}`, item: `${SITE}/translate/${l.slug}/${d.slug}` },
                { "@type": "ListItem", position: 5, name: loc.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(ratingKey, `${url}#service`, 2).map((r) => ({
            type: "application/ld+json" as const,
            children: JSON.stringify(r),
          })),
        ]),
      ],
    };
  },
  component: TripleKeyPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบหน้า</h1>
      <Link to="/translate" className="mt-4 inline-block text-gold underline">
        กลับไปแปลรายภาษา
      </Link>
    </main>
  ),
});

function TripleKeyPage() {
  const { langSlug, docSlug, locSlug } = Route.useLoaderData() as {
    langSlug: string; docSlug: string; locSlug: string;
  };
  const l = getTranslationLanguage(langSlug)!;
  const d = getTranslationDocType(docSlug)!;
  const loc = getLocation(locSlug)!;
  const otherLangs = TRANSLATION_LANGUAGES.filter((x) => x.slug !== langSlug).slice(0, 8);
  const otherDocs = TRANSLATION_DOCTYPES.filter((x) => x.slug !== docSlug).slice(0, 8);
  const otherLocs = TRIPLE_KEY_LOCATIONS.filter((x) => x !== locSlug).slice(0, 8);
  const price = l.priceFromTh + d.surcharge;

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/translate" className="hover:text-gold">แปลเอกสาร</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/translate/$language" params={{ language: l.slug }} className="hover:text-gold">{l.nameTh}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/translate/$language/$doctype" params={{ language: l.slug, doctype: d.slug }} className="hover:text-gold">{d.nameTh}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{loc.nameTh}</span>
          </nav>

          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Languages className="h-3.5 w-3.5" /> Certified · {d.nameEn} → {l.nameEn} · {loc.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            แปล{d.nameTh}เป็น{l.nameTh} สำหรับลูกค้า{loc.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            {d.description} — ราคาเริ่ม ฿{price.toLocaleString()}/หน้า เสร็จ {l.turnaround} พร้อมบริการรับ-ส่งเอกสารถึง{loc.nameTh}
          </p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคาเริ่ม", v: `฿${price.toLocaleString()}/หน้า` },
              { k: "เสร็จภายใน", v: l.turnaround },
              { k: "ปริมาณทั่วไป", v: d.pagesEstimate },
              { k: "พื้นที่บริการ", v: loc.nameTh },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> ส่ง{d.nameTh}ผ่าน LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service (DBD 0435567000061) ให้บริการแปล{d.nameTh}เป็น{l.nameTh}สำหรับลูกค้า{loc.nameTh} — รับรองโดยนักแปลขึ้นทะเบียน ราคาเริ่ม ฿{price.toLocaleString()}/หน้า เสร็จภายใน {l.turnaround} ใช้ยื่นสถานทูต กงสุล กระทรวงการต่างประเทศ ศาลได้ทันที จุดประสงค์: {d.intent}
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <MapPin className="inline h-6 w-6 text-amber-600" /> ขอบเขตงาน — {d.nameTh} → {l.nameTh}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
            {d.description} สำหรับลูกค้าใน{loc.nameTh}เราให้บริการครบวงจร — รับเอกสาร, แปลโดยนักแปลขึ้นทะเบียน, รับรอง, จัดส่ง — โดยไม่ต้องเสียเวลาเดินทาง
          </p>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Stamp className="inline h-6 w-6 text-emerald-600" /> ขั้นตอนรับรอง ({d.certifications.length} ขั้น)
          </h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2">
            {d.certifications.map((c, i) => (
              <li key={c} className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-emerald-600 text-xs font-bold text-white">{i + 1}</span>
                <div>
                  <div className="font-display text-sm font-extrabold text-navy-deep">{c}</div>
                  <div className="mt-0.5 text-[12px] text-slate-600">NYC จัดการให้ครบทุกขั้นตอน — ไม่ต้องวิ่งเอง</div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ปลายทางที่ใช้บ่อย</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {d.destinationUses.map((u) => (
              <li key={u} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {u}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ประเทศที่ใช้{l.nameTh}</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {l.countryOfUse.map((c) => (
              <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-navy-deep/15 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-navy-deep">
                <Globe2 className="h-3.5 w-3.5 text-amber-600" /> {c}
              </span>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคา · {loc.nameTh}</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            แปล{d.nameTh} → {l.nameTh} ฿{price.toLocaleString()}/หน้า
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ตราประทับ + ลายเซ็นนักแปลขึ้นทะเบียน</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> เสร็จภายใน {l.turnaround}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รับ-ส่ง EMS ทั่ว{loc.region}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รับรอง MFA + สถานทูตครบในแพ็กเดียว</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={[
              {
                q: `แปล${d.nameTh}เป็น${l.nameTh}ที่${loc.nameTh} ราคาเท่าไหร่?`,
                a: `เริ่ม ฿${price.toLocaleString()}/หน้า — รับรองโดยนักแปลขึ้นทะเบียน เสร็จ ${l.turnaround} ใช้ยื่นสถานทูต/กงสุล/MFA ได้ทันที`,
              },
              {
                q: `${d.nameTh}ต้องผ่านขั้นตอนรับรองอะไรบ้าง?`,
                a: `${d.certifications.join(" → ")} เราจัดให้ครบในแพ็กเกจเดียว`,
              },
              ...d.faqExtra,
            ]} />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสารอื่น → {l.nameTh} ({loc.nameTh})</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherDocs.map((od) => (
              <Link
                key={od.slug}
                to="/translate/$language/$doctype/in/$location"
                params={{ language: l.slug, doctype: od.slug, location: loc.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{od.nameEn}</div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">แปล{od.nameTh}</div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-amber-600" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{d.nameTh} → ภาษาอื่น ({loc.nameTh})</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherLangs.map((ol) => (
              <Link
                key={ol.slug}
                to="/translate/$language/$doctype/in/$location"
                params={{ language: ol.slug, doctype: d.slug, location: loc.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{ol.nameEn}</div>
                <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                  แปล{d.nameTh} → {ol.nameTh}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">เริ่ม ฿{(ol.priceFromTh + d.surcharge).toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">แปล{d.nameTh} → {l.nameTh} ในพื้นที่อื่น</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherLocs.map((ls) => {
              const other = getLocation(ls);
              if (!other) return null;
              return (
                <Link
                  key={ls}
                  to="/translate/$language/$doctype/in/$location"
                  params={{ language: l.slug, doctype: d.slug, location: ls }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">{other.region}</div>
                  <div className="mt-1 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    {other.nameTh}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </div>

      <TripleKeyCrossLinks
        mode="th"
        language={l.slug}
        doctype={d.slug}
        languageNameTh={l.nameTh}
        languageNameEn={l.nameEn}
        doctypeNameTh={d.nameTh}
        doctypeNameEn={d.nameEn}
      />

      <TrustLayer variant="compact" />
    </main>
  );
}
